import { IconCalendarMonth, IconInfoCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { KeyedError, getErrorsFromPromises } from '@zorro/common-utils';
import { Divider } from '@zorro/design-system/v2/Divider';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Group, Icon, Stack, Text } from '@zorro/web/design-system';
import {
  callEndpoint,
  handleBatchNotifications,
  showErrorNotification,
  useDynamicRouter,
  useIdempotencyKey,
  useLoadingOverlay,
} from '@zorro/web/ui-utils';
import _chunk from 'lodash/chunk';
import { useMemo, useState } from 'react';

import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

type WaiveCoverageFormBulkProps = {
  recordsForActions: RecordForActions[];
  onSuccess: () => void;
  onClose: () => void;
};

export const WaiveCoverageFormBulk = ({
  recordsForActions,
  onSuccess,
  onClose,
}: WaiveCoverageFormBulkProps) => {
  const idempotencyKey = useIdempotencyKey();
  const queryClient = useQueryClient();
  const { reloadPage } = useDynamicRouter({});
  const { startLoading, stopLoading } = useLoadingOverlay();

  const [errors, setErrors] = useState<KeyedError[]>([]);

  async function handleSubmit() {
    try {
      setErrors([]);
      startLoading();

      const employeeBatches = _chunk(recordsForActions, 5);

      const results: PromiseSettledResult<unknown>[] = [];

      for (const batch of employeeBatches) {
        const batchResults = await Promise.allSettled(
          batch.map(async ({ fullName, employeeId, onboardingPeriodId }) => {
            if (!onboardingPeriodId) {
              throw new Error(`${fullName} doesn't have an onboarding period`);
            }

            return callEndpoint({
              method: 'onboardingPeriodsControllerWaiveCoverage',
              params: [
                onboardingPeriodId,
                idempotencyKey.getFor(onboardingPeriodId),
                { employeeId },
              ],
            });
          })
        );

        results.push(...batchResults);
      }

      setTimeout(async () => {
        await queryClient.invalidateQueries();
      }, 0);
      reloadPage();

      const errorsFromPromises = getErrorsFromPromises(
        results,
        recordsForActions.map(({ fullName }) => ({ key: fullName }))
      );

      setErrors(errorsFromPromises);

      await handleBatchNotifications(
        results,
        { singular: 'employee' },
        'updated'
      );

      if (errorsFromPromises.length === 0) {
        onSuccess();
      }
    } catch {
      showErrorNotification({ message: 'Failed to update employees' });
    } finally {
      stopLoading();
    }
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit waiver"
        primaryButtonProps={{
          onClick: handleSubmit,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <Group wrap="nowrap" gap="xs">
        <IconCalendarMonth />
        <Text>Waiver effective dates</Text>
      </Group>
      <Text>
        Each employee's waiver will start from their expected coverage date and
        last until the end of the plan year
      </Text>
      <Divider my="md" />
      <Group wrap="nowrap" gap="xs">
        <Icon icon={IconInfoCircle} />
        <Text>
          Please make sure to collect evidence to each employee's waive election
          confirmation
        </Text>
      </Group>

      <ErrorsTable
        tableTitle="Employee name"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
